/**
 * Institute registration - SaaS: institutes register; students are added by admins
 * Integrates with POST /api/auth/register-institute
 */

import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ThemeSettings from '../components/ThemeSettings';

export default function Register() {
  const [form, setForm] = useState({
    instituteName: '',
    slug: '',
    email: '',
    contactName: '',
    instituteCode: '',
    title: '',
    parentSociety: '',
    establishedOn: '',
    naacAccredited: false,
    naacGrade: '',
    naacAccreditationYear: '',
    nbaAccredited: false,
    nbaAccreditationYear: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    logoUrl: '',
    brandingSentence: '',
    shortProfile: '',
    programsOfferedText: '',
    password: '',
    confirmPassword: '',
    adminFirstName: '',
    adminLastName: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { registerInstitute } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'instituteName'
        ? { slug: value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '') }
        : {}),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password !== form.confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    if (
      !/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*])/.test(form.password)
    ) {
      setError(
        'Password must contain uppercase, lowercase, number and special character'
      );
      return;
    }
    if (form.slug.length < 2) {
      setError('Institute slug must be at least 2 characters (e.g. my-institute)');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        instituteName: form.instituteName,
        slug: form.slug,
        email: form.email,
        contactName: form.contactName || undefined,
        instituteCode: form.instituteCode || undefined,
        title: form.title || form.instituteName,
        parentSociety: form.parentSociety || undefined,
        establishedOn: form.establishedOn || undefined,
        naacAccredited: form.naacAccredited,
        naacGrade: form.naacGrade || undefined,
        naacAccreditationYear: form.naacAccreditationYear ? Number(form.naacAccreditationYear) : undefined,
        nbaAccredited: form.nbaAccredited,
        nbaAccreditationYear: form.nbaAccreditationYear ? Number(form.nbaAccreditationYear) : undefined,
        address: form.address || undefined,
        city: form.city || undefined,
        state: form.state || undefined,
        country: form.country || undefined,
        pincode: form.pincode || undefined,
        logoUrl: form.logoUrl || undefined,
        brandingSentence: form.brandingSentence || undefined,
        shortProfile: form.shortProfile || undefined,
        programsOffered: form.programsOfferedText.split('\n').map((x) => x.trim()).filter(Boolean),
        password: form.password,
        adminFirstName: form.adminFirstName,
        adminLastName: form.adminLastName,
      };
      await registerInstitute(payload);
      navigate('/');
    } catch (err) {
      const code = err.response?.data?.error?.code;
      let msg =
        err.response?.data?.error?.message ||
        err.response?.data?.error?.details?.map((d) => d.message).join(', ') ||
        err.message ||
        'Registration failed';
      if (code === '28P01') {
        msg = 'Server database configuration error. Please try again later or contact support.';
      }
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <aside className="auth-brand" aria-hidden="true">
        <div className="auth-brand-content">
          <div className="auth-brand-logo">360</div>
          <h1 className="auth-brand-title">Start your institute</h1>
          <p className="auth-brand-text">
            Register once as an institute admin, then onboard students and faculty from your dashboard.
          </p>
        </div>
        <ul className="auth-brand-features">
          <li className="auth-brand-feature">
            <span className="auth-brand-feature-dot" />
            <span>Secure multi-tenant institute setup</span>
          </li>
          <li className="auth-brand-feature">
            <span className="auth-brand-feature-dot" />
            <span>Bulk student upload and user management</span>
          </li>
          <li className="auth-brand-feature">
            <span className="auth-brand-feature-dot" />
            <span>Feedback cycles, reports, and analytics</span>
          </li>
        </ul>
      </aside>

      <div className="auth-panel relative">
        <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-10">
          <div
            className="p-2 rounded-lg"
            style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border-color)' }}
          >
            <ThemeSettings />
          </div>
        </div>

        <div className="auth-card max-w-lg">
          <div className="auth-mobile-brand">
            <div className="auth-mobile-logo">360</div>
            <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
              Institute registration
            </p>
          </div>

          <div className="auth-card-inner">
            <div className="text-center mb-8">
              <h2 className="page-title text-xl sm:text-2xl">Register your institute</h2>
              <p className="page-subtitle">
                Create your institute account. You will be the first admin.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="alert-error" role="alert">
                  {error}
                </div>
              )}
              <div>
                <label className="input-label" htmlFor="instituteName">
                  Institute name
                </label>
                <input
                  id="instituteName"
                  type="text"
                  name="instituteName"
                  value={form.instituteName}
                  onChange={handleChange}
                  required
                  minLength={2}
                  className="input"
                  placeholder="e.g. ABC College"
                />
              </div>
              <div>
                <label className="input-label" htmlFor="slug">
                  Institute slug
                </label>
                <input
                  id="slug"
                  type="text"
                  name="slug"
                  value={form.slug}
                  onChange={handleChange}
                  required
                  minLength={2}
                  pattern="[a-z0-9]+(-[a-z0-9]+)*"
                  className="input"
                  placeholder="e.g. abc-college"
                />
                <p className="input-hint">
                  Lowercase letters, numbers, hyphens only. Used for login with multiple institutes.
                </p>
              </div>
              <div>
                <label className="input-label" htmlFor="email">
                  Admin email
                </label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="input"
                  placeholder="admin@institute.edu"
                />
              </div>
              <div>
                <label className="input-label" htmlFor="contactName">
                  Contact name <span style={{ color: 'var(--text-muted)' }} className="font-normal">(optional)</span>
                </label>
                <input
                  id="contactName"
                  type="text"
                  name="contactName"
                  value={form.contactName}
                  onChange={handleChange}
                  className="input"
                  placeholder="Admin name"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="input-label" htmlFor="instituteCode">Institute code</label>
                  <input id="instituteCode" name="instituteCode" value={form.instituteCode} onChange={handleChange} className="input" placeholder="e.g. ABC-2026" />
                </div>
                <div>
                  <label className="input-label" htmlFor="title">Display title</label>
                  <input id="title" name="title" value={form.title} onChange={handleChange} className="input" placeholder="Official display name" />
                </div>
                <div>
                  <label className="input-label" htmlFor="parentSociety">Parent society</label>
                  <input id="parentSociety" name="parentSociety" value={form.parentSociety} onChange={handleChange} className="input" />
                </div>
                <div>
                  <label className="input-label" htmlFor="establishedOn">Established on</label>
                  <input id="establishedOn" type="date" name="establishedOn" value={form.establishedOn} onChange={handleChange} className="input" />
                </div>
                <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <input type="checkbox" name="naacAccredited" checked={form.naacAccredited} onChange={handleChange} />
                  NAAC accredited
                </label>
                <label className="flex items-center gap-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                  <input type="checkbox" name="nbaAccredited" checked={form.nbaAccredited} onChange={handleChange} />
                  NBA accredited
                </label>
                <div>
                  <label className="input-label" htmlFor="naacGrade">NAAC grade</label>
                  <input id="naacGrade" name="naacGrade" value={form.naacGrade} onChange={handleChange} className="input" placeholder="A+" />
                </div>
                <div>
                  <label className="input-label" htmlFor="naacAccreditationYear">NAAC year</label>
                  <input id="naacAccreditationYear" type="number" name="naacAccreditationYear" value={form.naacAccreditationYear} onChange={handleChange} className="input" />
                </div>
                <div>
                  <label className="input-label" htmlFor="nbaAccreditationYear">NBA year</label>
                  <input id="nbaAccreditationYear" type="number" name="nbaAccreditationYear" value={form.nbaAccreditationYear} onChange={handleChange} className="input" />
                </div>
                <div>
                  <label className="input-label" htmlFor="logoUrl">Logo URL</label>
                  <input id="logoUrl" name="logoUrl" value={form.logoUrl} onChange={handleChange} className="input" placeholder="https://..." />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="input-label" htmlFor="address">Institute address</label>
                  <textarea id="address" name="address" value={form.address} onChange={handleChange} className="input min-h-[88px]" />
                </div>
                <input name="city" value={form.city} onChange={handleChange} className="input" placeholder="City" />
                <input name="state" value={form.state} onChange={handleChange} className="input" placeholder="State" />
                <input name="country" value={form.country} onChange={handleChange} className="input" placeholder="Country" />
                <input name="pincode" value={form.pincode} onChange={handleChange} className="input" placeholder="Pincode" />
                <div className="sm:col-span-2">
                  <label className="input-label" htmlFor="brandingSentence">Branding sentence</label>
                  <input id="brandingSentence" name="brandingSentence" value={form.brandingSentence} onChange={handleChange} className="input" />
                </div>
                <div>
                  <label className="input-label" htmlFor="programsOfferedText">Programs offered</label>
                  <textarea id="programsOfferedText" name="programsOfferedText" value={form.programsOfferedText} onChange={handleChange} className="input min-h-[88px]" placeholder="One program per line" />
                </div>
                <div>
                  <label className="input-label" htmlFor="shortProfile">Short profile</label>
                  <textarea id="shortProfile" name="shortProfile" value={form.shortProfile} onChange={handleChange} className="input min-h-[88px]" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="input-label" htmlFor="adminFirstName">
                    Admin first name
                  </label>
                  <input
                    id="adminFirstName"
                    type="text"
                    name="adminFirstName"
                    value={form.adminFirstName}
                    onChange={handleChange}
                    required
                    minLength={2}
                    className="input"
                  />
                </div>
                <div>
                  <label className="input-label" htmlFor="adminLastName">
                    Admin last name
                  </label>
                  <input
                    id="adminLastName"
                    type="text"
                    name="adminLastName"
                    value={form.adminLastName}
                    onChange={handleChange}
                    required
                    minLength={2}
                    className="input"
                  />
                </div>
              </div>
              <div>
                <label className="input-label" htmlFor="password">
                  Password
                </label>
                <input
                  id="password"
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  minLength={8}
                  className="input"
                  placeholder="Min 8 chars, upper, lower, number, special"
                />
              </div>
              <div>
                <label className="input-label" htmlFor="confirmPassword">
                  Confirm password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  name="confirmPassword"
                  value={form.confirmPassword}
                  onChange={handleChange}
                  required
                  className="input"
                />
              </div>
              <button type="submit" disabled={submitting} className="btn-primary w-full py-3">
                {submitting ? 'Registering…' : 'Register institute'}
              </button>
            </form>

            <p className="mt-6 text-center text-sm" style={{ color: 'var(--text-secondary)' }}>
              Already have an account?{' '}
              <Link to="/login" className="auth-footer-link">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
